import { useRouteError, useNavigate } from 'react-router-dom'

/**
 * Route-level error boundary.
 *
 * Without an `errorElement`, React Router falls back to its own bare
 * "Unexpected Application Error!" screen with a raw minified stack — which is
 * what a single bad render or effect cleanup produced on the Ask page. That is
 * both unreadable for users and unrecoverable without a manual reload.
 *
 * This keeps the chrome, says something human, and offers a way out. The raw
 * error stays visible in dev (and in the console either way) so it is still
 * diagnosable.
 */
export default function RouteErrorBoundary() {
    const error = useRouteError()
    const navigate = useNavigate()

    const detail =
        error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : null

    return (
        <div
            role="alert"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                padding: '24px',
                textAlign: 'center',
                gap: '12px',
            }}
        >
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
                Something went wrong
            </h1>
            <p style={{ margin: 0, maxWidth: '480px' }}>
                That page didn&apos;t load properly. Trying again usually sorts
                it — if it keeps happening, let us know.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button type="button" onClick={() => navigate(0)}>
                    Try again
                </button>
                <button type="button" onClick={() => navigate('/')}>
                    Go home
                </button>
            </div>
            {import.meta.env.DEV && detail ? (
                <pre
                    style={{
                        marginTop: '16px',
                        maxWidth: '90vw',
                        overflowX: 'auto',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        opacity: 0.7,
                    }}
                >
                    {detail}
                </pre>
            ) : null}
        </div>
    )
}
